import { create } from "zustand";

// One highlighted passage selected on the full-solution preview: the text to
// assign + its page-relative rectangles (a selection can span lines/pages), used
// to draw the green overlay.
export interface SelectionPortion {
  text: string;
  rects: { page: number; box: [number, number, number, number] }[]; // x0,y0,x1,y1 norm 0..1
}

export type ModelView = "original" | "digitized";

// Shared state between the Model-solution cards (right rail) and the centre.
//  * highlightTaskId / editingTaskId — drive the per-task paper stack (green
//    frame + manual editor) in normal mode.
//  * assignMode — when on, the centre shows the uploaded full solution and Kim
//    highlights passages to assign to tasks.
//  * modelView — original upload vs digitized (OCR'd) copy in Assign mode.
//  * portions — the currently-highlighted passages awaiting assignment.
interface ModelUiState {
  highlightTaskId: number | null;
  editingTaskId: number | null;
  setHighlight: (id: number | null) => void;
  toggleHighlight: (id: number) => void;
  setEditing: (id: number | null) => void;
  assignMode: boolean;
  setAssignMode: (on: boolean) => void;
  modelView: ModelView;
  setModelView: (v: ModelView) => void;
  portions: SelectionPortion[];
  addPortion: (p: SelectionPortion) => void;
  replacePortions: (p: SelectionPortion[]) => void;
  clearPortions: () => void;
  reset: () => void;
}

export const useModelUiStore = create<ModelUiState>((set) => ({
  highlightTaskId: null,
  editingTaskId: null,
  setHighlight: (id) => set({ highlightTaskId: id }),
  toggleHighlight: (id) =>
    set((s) => ({ highlightTaskId: s.highlightTaskId === id ? null : id })),
  // Editing a task also highlights it so the centre scrolls it into view.
  setEditing: (id) => set({ editingTaskId: id, highlightTaskId: id }),
  assignMode: false,
  setAssignMode: (on) => set({ assignMode: on, portions: [] }),
  modelView: "original",
  setModelView: (v) => set({ modelView: v }),
  portions: [],
  addPortion: (p) => set((s) => ({ portions: [...s.portions, p] })),
  replacePortions: (p) => set({ portions: p }),
  clearPortions: () => set({ portions: [] }),
  reset: () =>
    set({
      highlightTaskId: null,
      editingTaskId: null,
      assignMode: false,
      modelView: "original",
      portions: [],
    }),
}));

/** Joined text of all pending portions — the solution text an Assign would store. */
export function portionsText(portions: SelectionPortion[]): string {
  return portions.map((p) => p.text.trim()).filter(Boolean).join("\n\n");
}
